import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { partnerApi, TOS_VERSION_CURRENT } from "@/lib/partner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function PartnerApply() {
  const nav = useNavigate();
  const { user, ready } = useAuth();
  const [tos, setTos] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState("manual_bank");
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState({ loading: true, mode: null, partner: null });

  // Bootstrap: figure out current partner state and route accordingly.
  useEffect(() => {
    if (!ready) return;
    if (!user) {
      // Unauthenticated → login. Normalize to the partner-aware URL so the
      // login page renders partner-context copy and post-auth returns to the
      // wizard (defensive: outer ProtectedRoute intercepts first, but session
      // expiry after mount can still hit this branch).
      nav("/login?intent=partner&return_to=/partners/apply", { replace: true });
      return;
    }
    let cancelled = false;
    partnerApi.me()
      .then((data) => {
        if (cancelled) return;
        const status = data?.partner?.status;
        if (status === "active") {
          nav("/partner/dashboard", { replace: true });
        } else if (status === "suspended") {
          setState({ loading: false, mode: "suspended", partner: data.partner });
        } else if (status === "pending") {
          setState({ loading: false, mode: "pending", partner: data.partner });
        } else {
          setState({ loading: false, mode: "apply", partner: null });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        // 404 not_a_partner → show apply form
        if (e?.response?.status === 404) {
          setState({ loading: false, mode: "apply", partner: null });
        } else {
          setState({ loading: false, mode: "apply", partner: null });
        }
      });
    return () => { cancelled = true; };
  }, [ready, user, nav]);

  const submit = async () => {
    if (!tos) return toast.error("Please accept the Partner Terms to continue.");
    setBusy(true);
    try {
      const res = await partnerApi.apply({
        tos_accepted: true,
        tos_version: TOS_VERSION_CURRENT,
        payout_method: payoutMethod,
      });
      if (res?.partner?.status === "pending") {
        toast.success("Application submitted. Awaiting approval.");
        nav("/partner/dashboard");
      } else if (res?.partner) {
        nav("/partner/dashboard");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit application.");
    } finally { setBusy(false); }
  };

  if (!ready || state.loading) {
    return <div className="mx-auto max-w-2xl px-6 py-16 text-white/60" data-testid="partner-apply-loading">Loading…</div>;
  }

  if (state.mode === "pending") {
    return (
      <div className="mx-auto max-w-lg px-6 py-16 text-white" data-testid="partner-apply-pending">
        <h1 className="text-2xl font-light">Application under review</h1>
        <p className="mt-3 text-sm text-white/60">
          Your Partner Program application is being reviewed. You&rsquo;ll get an email as soon as it&rsquo;s approved,
          usually the same day.
        </p>
        <button onClick={() => nav("/partner/dashboard")}
                data-testid="partner-apply-pending-dashboard"
                className="mt-6 rounded-lg border border-white/15 px-4 py-2 text-sm text-white/80 hover:bg-white/5">
          Go to partner dashboard
        </button>
      </div>
    );
  }

  if (state.mode === "suspended") {
    return (
      <div className="mx-auto max-w-lg px-6 py-16 text-white" data-testid="partner-apply-suspended">
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5">
          <h1 className="text-xl font-medium text-red-200">Partner account suspended</h1>
          <p className="mt-3 text-sm leading-relaxed text-red-100/80">
            Your partner account is currently suspended. Attribution and new commissions are paused.
            If you believe this was in error, contact <a href="mailto:partners@tappresence.com" className="underline">partners@tappresence.com</a>.
          </p>
        </div>
      </div>
    );
  }

  // mode === "apply"
  return (
    <div className="mx-auto max-w-2xl px-6 py-16 text-white" data-testid="partner-apply-page">
      <h1 className="text-3xl font-light tracking-tight">Become a TapPresence Sales Partner</h1>
      <p className="mt-3 text-sm text-white/60">
        Earn commissions on every paid subscription you refer. Commissions start at 15% and scale up
        to 30% as your active-seat count grows. Refunds and chargebacks reverse automatically.
      </p>

      <div className="mt-8 space-y-5 rounded-2xl border border-white/10 bg-[#0B0D12] p-6">
        <div>
          <label className="block text-xs uppercase tracking-wider text-white/45">Payout method</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {[
              { k: "manual_bank", label: "Bank transfer" },
              { k: "paypal", label: "PayPal" },
              { k: "stripe_connect", label: "Stripe Connect (later)" },
            ].map((o) => (
              <button
                key={o.k}
                type="button"
                onClick={() => setPayoutMethod(o.k)}
                data-testid={`payout-${o.k}`}
                className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  payoutMethod === o.k
                    ? "border-[#D6A653] bg-[#D6A653]/12 text-[#D6A653]"
                    : "border-white/12 text-white/55 hover:text-white"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-white/40">
            You can complete the payout and tax details later — required before your first payout.
          </p>
        </div>

        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={tos}
            onChange={(e) => setTos(e.target.checked)}
            className="mt-1 h-4 w-4 accent-[#D6A653]"
            data-testid="partner-apply-tos"
          />
          <span className="text-white/70">
            I accept the{" "}
            <a href="/legal/partner-terms" target="_blank" rel="noreferrer" className="text-[#D6A653] underline underline-offset-2" data-testid="partner-apply-tos-link">
              Partner Program Terms
            </a>{" "}
            (v{TOS_VERSION_CURRENT}), including clawback of commissions for refunded or charged-back
            payments.
          </span>
        </label>

        <div className="pt-2">
          <Button
            onClick={submit}
            disabled={busy || !tos}
            data-testid="partner-apply-submit"
            className="w-full bg-[#D6A653] text-black hover:bg-[#c99a49]"
          >
            {busy ? "Submitting…" : "Submit application"}
          </Button>
        </div>
      </div>
    </div>
  );
}
